import { FormProvider, useForm } from 'react-hook-form';
import React, { useEffect, useState } from 'react';

import TENANT_FORM_SCHEMA from 'Constants/schemas/tenantFormSchema';
import TENANT_INITIAL_STATE from 'Constants/tenantFormInitialState';
import { fieldArrayToCodeFlags } from 'Utils/codeFlags';
import { any } from 'prop-types';
import { notification } from 'antd';
import useAddTenant from 'Hooks/useAddTenant';
import useDeleteTenant from 'Hooks/useDeleteTenant';
import useDeleteTenantImage from 'Hooks/useDeleteTenantImage';
import useUpdateTenant from 'Hooks/useUpdateTenant';
import useUpdateTenantImage from 'Hooks/useUpdateTenantImage';
import { yupResolver } from '@hookform/resolvers/yup';

export const TenantFormProvider = ({ children }) => {
  const addTenant = useAddTenant();
  const deleteTenant = useDeleteTenant();
  const updateTenant = useUpdateTenant();
  const updateImage = useUpdateTenantImage();
  const deleteImage = useDeleteTenantImage();

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: TENANT_INITIAL_STATE,
    resolver: yupResolver(TENANT_FORM_SCHEMA),
  });

  const setResponseErrors = (err) => {
    switch (err.message) {
      case 'Tenant already exists with identifier: 0adb973a-d7e0-424d-a16b-7d5820e842c9':
        return methods.setError(
          'identifier',
          {
            type: 'focus',
            message: err.message,
          },
          { shouldFocus: true }
        );
      default:
        return;
    }
  };

  const openNotificationWithIcon = ({ action, type, message, description, onClick }) => {
    return notification[type]({ message, description, onClick });
  };

  const onClear = async () => {
    let obj = _.clone(TENANT_INITIAL_STATE);
    await methods.reset(TENANT_INITIAL_STATE);
  };
  const onCancel = () => alert('cancelled');

  const onSave = async () => {
    const { logo, ...rest } = await methods.getValues();
    updateTenant.mutate(methods.getValues(), {
      onSuccess: () => {
        openNotificationWithIcon({
          type: 'success',
          message: `Successfully Saved`,
          description: `${rest.name} was successfully saved.`,
          onClick: () => {
            console.log('Notification Clicked!');
          },
        });
        methods.reset(rest);
      },
      onError: (err) => {
        setResponseErrors(err);
        openNotificationWithIcon({
          type: 'error',
          message: `Error Saving`,
          description: err.message,
        });
        console.log('ONSAVE::ERROR: ', err);
      },
    });
  };

  useEffect(() => {
    methods.reset(TENANT_INITIAL_STATE);
    return () => methods.reset(TENANT_INITIAL_STATE);
  }, []);

  const onUpdateImage = async () => {
    const fields = methods.getValues();
    updateImage.mutate(methods.getValues(), {
      onSuccess: (d) => {
        openNotificationWithIcon({
          type: 'success',
          message: `Successfully Updated Tenant`,
          description: `${fields.name} was successfully updated.  Changes may take up to 5 min to take effect.`,
        });
      },
      onError: (err) => {
        setResponseErrors(err);
        openNotificationWithIcon({
          type: 'error',
          message: `Error Updating Logo`,
          description: err.response?.data?.message || err.message,
        });
      },
    });
  };

  const onDeleteImage = () => {
    const [identifier, branch] = methods.getValues(['identifier', 'branch']);
    const fields = methods.getValues();

    deleteImage.mutate(
      { identifier, branch },
      {
        onSuccess: () => {
          openNotificationWithIcon({
            type: 'success',
            message: `Successfully Updated Tenant`,
            description: `${fields.name} was successfully updated.  Changes may take up to 5 min to take effect.`,
          });
        },
        onError: (err) => {
          setResponseErrors(err);
          openNotificationWithIcon({
            type: 'error',
            message: `Error Deleting Logo`,
            description: err.response?.data?.message || err.message,
          });
        },
      }
    );
  };

  const onUpdate = async () => {
    const fields = await methods.getValues();
    let result = _.omit(fields, ['homePageConfiguration.logoFormat']);
    result.codeFlags = fieldArrayToCodeFlags(fields.codeFlags);
    updateTenant.mutate(result, {
      onSuccess: () => {
        if (fields?.homePageConfiguration?.logoFormat) {
          updateImage.mutate(fields, {
            onError: (err) => {
              openNotificationWithIcon({
                type: 'error',
                message: `Error Updating Tenant Logo`,
                description: err.response?.data?.message || err.message,
              });
            },
            onSuccess: (d) => {
              openNotificationWithIcon({
                type: 'success',
                message: `Successfully Updated Tenant`,
                description: `${fields.name} and logo was successfully updated.  Changes may take up to 5 min to take effect.`,
              });
            },
          });
        } else {
          openNotificationWithIcon({
            type: 'success',
            message: `Successfully Updated Tenant`,
            description: `${fields.name} was successfully updated.`,
          });
        }
      },
      onError: (err) => {
        openNotificationWithIcon({
          type: 'error',
          message: `Error Updating Tenant`,
          description: err.response?.data?.message || err.message,
        });
        console.log('ONUPDATE::Error: ', err.message);
      },
    });
  };

  const onDelete = () => {
    const [identifier, branch] = methods.getValues(['identifier', 'branch']);
    const tenant = methods.getValues();

    if (window.confirm(`Are you sure you want to delete ${identifier} tenant in ${branch} env?`)) {
      deleteTenant.mutate(
        { identifier, branch },
        {
          onSuccess: () => {
            openNotificationWithIcon({
              type: 'success',
              message: `Successfully Deleted Logo`,
              description: `Logo for ${tenant.name} was removed.  Changes may take up to 5 min to take effect.`,
              onClick: () => {
                console.log('Notification Clicked!');
              },
            });
          },
          onError: (err) => {
            setResponseErrors(err);
            openNotificationWithIcon({
              type: 'error',
              message: `Error Deleting Logo`,
              description: err.response?.data?.message || err.message,
            });
          },
        }
      );
    }
  };

  const onSubmit = async () => {
    const fields = await methods.getValues();
    let result = _.omit(fields, ['homePageConfiguration.logoFormat']);

    if (fields?.homePageConfiguration?.branding?.branding === 'tenant') {
      result = _.omit(fields, ['homePageConfiguration.branding.details']);
    }
    result.codeFlags = fieldArrayToCodeFlags(fields.codeFlags);
    addTenant.mutate(result, {
      onSuccess: (res) => {
        if (fields?.homePageConfiguration?.logoFormat?.length) {
          updateImage.mutate(fields, {
            onError: (err) => {
              setResponseErrors(err);
              openNotificationWithIcon({
                type: 'error',
                message: `Error updating logo`,
                description: err.response?.data?.message || err.message,
              });
            },
            onSuccess: (d) => {
              openNotificationWithIcon({
                type: 'success',
                message: `Successfully Added Tenant`,
                description: `A new Tenant and logo have been created.`,
              });
            },
          });
        } else {
          openNotificationWithIcon({
            type: 'success',
            message: `Successfully Added Tenant`,
            description: `Name: ${fields.name}\ Id: ${fields.identifier}`,
          });
        }
      },
      onError: (err) => {
        openNotificationWithIcon({
          action: 'open',
          type: 'error',
          message: `Error Adding Tenant`,
          description: err.response?.data?.message || err.message,
        });
      },
    });
  };

  return (
    <FormProvider
      {...methods}
      state={methods.formState}
      isSubmitting={methods.formState.isSubmitting}
      onSave={onUpdate}
      onCancel={onCancel}
      onClear={onClear}
      onDelete={onDelete}
      onDeleteLogo={onDeleteImage}
      onUpdateImage={onUpdateImage}
      initialState={TENANT_INITIAL_STATE}
      onUpdate={methods.handleSubmit(onUpdate)}
      onPublish={methods.handleSubmit(onSubmit)}
      onSubmit={methods.handleSubmit(onSubmit)}
    >
      {children}
    </FormProvider>
  );
};

TenantFormProvider.propTypes = {
  children: any,
};
